import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiShield, FiToggleLeft, FiToggleRight, FiSliders, FiAlertTriangle,
  FiCheck, FiSave, FiInfo, FiFilter, FiFlag, FiEye, FiZap,
  FiMessageSquare, FiBook, FiLock, FiUnlock, FiRefreshCw, FiList,
} = FiIcons;

// ── Storage key ───────────────────────────────────────────────────────────────
const STORAGE_KEY = 'em_content_moderation_settings';

const loadSettings = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionCard = ({ icon, title, subtitle, children, highlight = false }) => (
  <div className={`rounded-[32px] border p-8 space-y-6 ${highlight ? 'bg-accent-navy border-accent-navy/20' : 'bg-white border-brand-dark'}`}>
    <div className={`flex items-center gap-4 pb-5 border-b ${highlight ? 'border-white/10' : 'border-brand-dark'}`}>
      <div className={`p-3 rounded-2xl ${highlight ? 'bg-white/10' : 'bg-brand-light'}`}>
        <SafeIcon icon={icon} className={`w-5 h-5 ${highlight ? 'text-accent-gold' : 'text-accent-navy'}`} />
      </div>
      <div>
        <h3 className={`font-serif text-lg ${highlight ? 'text-white' : 'text-accent-navy'}`}>{title}</h3>
        {subtitle && (
          <p className={`text-[10px] font-black uppercase tracking-widest ${highlight ? 'text-white/40' : 'text-text-muted'}`}>{subtitle}</p>
        )}
      </div>
    </div>
    {children}
  </div>
);

const ToggleRow = ({ label, desc, value, onChange, tag = null, warn = false }) => (
  <div className={`flex items-center justify-between gap-4 p-5 rounded-2xl border transition-colors group ${warn ? 'bg-red-50 border-red-200 hover:border-red-400' : 'bg-brand-light border-brand-dark hover:border-accent-gold/50'}`}>
    <div className="flex-grow min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className={`font-bold text-sm group-hover:transition-colors ${warn ? 'text-red-700' : 'text-accent-navy group-hover:text-accent-gold'}`}>{label}</p>
        {tag && (
          <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${tag === 'Global' ? 'bg-accent-navy text-white' : tag === 'Recommended' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {tag}
          </span>
        )}
      </div>
      <p className={`text-[11px] leading-relaxed mt-0.5 ${warn ? 'text-red-500' : 'text-text-muted'}`}>{desc}</p>
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-12 h-6 rounded-full relative flex-shrink-0 transition-colors duration-300 ${value ? (warn ? 'bg-red-500' : 'bg-accent-gold') : 'bg-brand-dark'}`}
    >
      <motion.div
        animate={{ x: value ? 24 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
      />
    </button>
  </div>
);

const SensitivitySlider = ({ label, desc, value, onChange, levels }) => (
  <div className="space-y-3 p-5 bg-brand-light rounded-2xl border border-brand-dark">
    <div className="flex items-center justify-between">
      <div>
        <p className="font-bold text-accent-navy text-sm">{label}</p>
        <p className="text-[10px] text-text-muted">{desc}</p>
      </div>
      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${levels[value].color}`}>
        {levels[value].label}
      </span>
    </div>
    <div className="relative pt-1">
      <input
        type="range" min="0" max={levels.length - 1} step="1"
        value={value} onChange={e => onChange(Number(e.target.value))}
        className="w-full accent-[#C5A880] cursor-pointer"
      />
      <div className="flex justify-between mt-1">
        {levels.map((l, i) => (
          <span key={i} className={`text-[9px] font-black uppercase tracking-widest ${i === value ? 'text-accent-gold' : 'text-text-muted/50'}`}>
            {l.label}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const WordlistEditor = ({ words, onAdd, onRemove }) => {
  const [input, setInput] = useState('');
  const submit = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !words.includes(trimmed)) { onAdd(trimmed); }
    setInput('');
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), submit())}
          placeholder="Add a blocked word or phrase…"
          className="flex-grow px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs"
        />
        <button
          type="button"
          onClick={submit}
          className="px-5 py-2 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-colors"
        >
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[40px]">
        {words.length === 0 && (
          <p className="text-[10px] text-text-muted italic">No custom blocked words added yet.</p>
        )}
        {words.map(w => (
          <span key={w} className="flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-600 px-3 py-1 rounded-full text-[10px] font-bold">
            {w}
            <button type="button" onClick={() => onRemove(w)} className="hover:text-red-800 transition-colors ml-0.5">×</button>
          </span>
        ))}
      </div>
    </div>
  );
};

const SaveToast = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.9 }}
        className="fixed bottom-8 right-8 z-[200] bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiCheck} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest">Settings Saved</p>
          <p className="text-[10px] opacity-80">Moderation configuration updated globally.</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Sensitivity level definitions ─────────────────────────────────────────────
const SENSITIVITY_LEVELS = [
  { label: 'Off',    color: 'bg-gray-100 text-gray-500 border-gray-200'   },
  { label: 'Low',    color: 'bg-green-100 text-green-700 border-green-200' },
  { label: 'Medium', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { label: 'High',   color: 'bg-red-100 text-red-700 border-red-200'       },
  { label: 'Strict', color: 'bg-purple-100 text-purple-700 border-purple-200' },
];

// ── STAT PILL ─────────────────────────────────────────────────────────────────
const StatPill = ({ icon, label, value, color }) => (
  <div className={`flex items-center gap-3 p-5 rounded-[20px] border border-brand-dark bg-white`}>
    <div className={`p-2.5 rounded-xl ${color}`}>
      <SafeIcon icon={icon} className="w-4 h-4 text-white" />
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{label}</p>
      <p className="text-xl font-serif text-accent-navy">{value}</p>
    </div>
  </div>
);

// ── SECTION NAV ───────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'approval',    label: 'Approval Rules',     icon: FiShield       },
  { id: 'filters',     label: 'Content Filters',    icon: FiFilter       },
  { id: 'wordlist',    label: 'Blocked Words',       icon: FiList         },
  { id: 'flagging',    label: 'Auto-Flagging',       icon: FiFlag         },
];

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const ContentModerationSettings = () => {
  const saved = loadSettings();

  // ── Approval toggles ────────────────────────────────────────────────────────
  const [globalAutoApproveTributes, setGlobalAutoApproveTributes] = useState(saved.globalAutoApproveTributes ?? false);
  const [globalAutoApproveGuestbook, setGlobalAutoApproveGuestbook] = useState(saved.globalAutoApproveGuestbook ?? false);
  const [allowOwnerOverride, setAllowOwnerOverride] = useState(saved.allowOwnerOverride ?? true);
  const [autoApproveVerifiedUsers, setAutoApproveVerifiedUsers] = useState(saved.autoApproveVerifiedUsers ?? true);
  const [requireEmailVerification, setRequireEmailVerification] = useState(saved.requireEmailVerification ?? false);
  const [allowAnonymousTributes, setAllowAnonymousTributes] = useState(saved.allowAnonymousTributes ?? true);

  // ── Filter sensitivity ──────────────────────────────────────────────────────
  const [profanityLevel, setProfanityLevel] = useState(saved.profanityLevel ?? 2);
  const [spamLevel, setSpamLevel] = useState(saved.spamLevel ?? 2);
  const [toxicityLevel, setToxicityLevel] = useState(saved.toxicityLevel ?? 3);
  const [linkBlocking, setLinkBlocking] = useState(saved.linkBlocking ?? true);
  const [capsFilter, setCapsFilter] = useState(saved.capsFilter ?? true);
  const [duplicateFilter, setDuplicateFilter] = useState(saved.duplicateFilter ?? true);

  // ── Blocked words ───────────────────────────────────────────────────────────
  const [blockedWords, setBlockedWords] = useState(saved.blockedWords ?? []);

  // ── Auto-flagging ───────────────────────────────────────────────────────────
  const [flagExternalLinks, setFlagExternalLinks] = useState(saved.flagExternalLinks ?? true);
  const [flagShortMessages, setFlagShortMessages] = useState(saved.flagShortMessages ?? true);
  const [flagRepeatedContent, setFlagRepeatedContent] = useState(saved.flagRepeatedContent ?? true);
  const [flagAnonymous, setFlagAnonymous] = useState(saved.flagAnonymous ?? false);
  const [minMessageLength, setMinMessageLength] = useState(saved.minMessageLength ?? 10);
  const [notifyAdminOnFlag, setNotifyAdminOnFlag] = useState(saved.notifyAdminOnFlag ?? true);

  // ── UI ──────────────────────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState('approval');
  const [showToast, setShowToast] = useState(false);

  const handleSave = () => {
    const payload = {
      globalAutoApproveTributes, globalAutoApproveGuestbook,
      allowOwnerOverride, autoApproveVerifiedUsers,
      requireEmailVerification, allowAnonymousTributes,
      profanityLevel, spamLevel, toxicityLevel,
      linkBlocking, capsFilter, duplicateFilter,
      blockedWords,
      flagExternalLinks, flagShortMessages, flagRepeatedContent,
      flagAnonymous, minMessageLength, notifyAdminOnFlag,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

    // Broadcast so SignGuestbookModal / TabTributes can react in real-time
    window.dispatchEvent(new CustomEvent('moderation_settings_updated', { detail: payload }));

    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const resetToDefaults = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  return (
    <div className="space-y-8">

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatPill icon={FiShield}       label="Auto-Approved Today"  value="142"  color="bg-green-500"   />
        <StatPill icon={FiFlag}         label="Auto-Flagged Today"   value="18"   color="bg-amber-500"   />
        <StatPill icon={FiEye}          label="Awaiting Review"      value="7"    color="bg-accent-navy" />
        <StatPill icon={FiAlertTriangle} label="Rejected This Week"  value="3"    color="bg-red-500"     />
      </div>

      {/* Global status banner */}
      <div className={`flex items-start gap-4 p-5 rounded-[24px] border-2 ${globalAutoApproveTributes && globalAutoApproveGuestbook ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`}>
        <div className={`p-2.5 rounded-xl flex-shrink-0 ${globalAutoApproveTributes && globalAutoApproveGuestbook ? 'bg-amber-500' : 'bg-green-500'}`}>
          <SafeIcon icon={globalAutoApproveTributes && globalAutoApproveGuestbook ? FiUnlock : FiLock} className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className={`font-black text-xs uppercase tracking-widest ${globalAutoApproveTributes && globalAutoApproveGuestbook ? 'text-amber-800' : 'text-green-800'}`}>
            {globalAutoApproveTributes && globalAutoApproveGuestbook
              ? 'Global Auto-Approval Active — All content publishes without review'
              : 'Manual Review Mode — Content is held for approval before publishing'}
          </p>
          <p className={`text-[11px] mt-0.5 ${globalAutoApproveTributes && globalAutoApproveGuestbook ? 'text-amber-600' : 'text-green-600'}`}>
            {globalAutoApproveTributes && globalAutoApproveGuestbook
              ? 'Individual memorial owners can still override this with their own approval settings.'
              : 'This is the safest setting. Memorial owners can relax this per-memorial.'}
          </p>
        </div>
      </div>

      {/* Section nav */}
      <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-1">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
          >
            <SafeIcon icon={s.icon} className="w-3.5 h-3.5" />
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeSection}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >

          {/* ── APPROVAL RULES ─────────────────────────────────────────── */}
          {activeSection === 'approval' && (
            <>
              <SectionCard icon={FiShield} title="Global Tribute Approval" subtitle="Platform-wide defaults for incoming tributes">
                <div className="space-y-3">
                  <ToggleRow
                    label="Auto-Approve All Tributes Globally"
                    desc="Bypasses manual review for tributes across every memorial. Memorial-level settings are overridden."
                    value={globalAutoApproveTributes}
                    onChange={setGlobalAutoApproveTributes}
                    tag="Global"
                    warn={globalAutoApproveTributes}
                  />
                  <ToggleRow
                    label="Auto-Approve All Guestbook Entries Globally"
                    desc="All guestbook sign-ins publish immediately without any moderation step."
                    value={globalAutoApproveGuestbook}
                    onChange={setGlobalAutoApproveGuestbook}
                    tag="Global"
                    warn={globalAutoApproveGuestbook}
                  />
                  <ToggleRow
                    label="Allow Memorial Owners to Override Approval Setting"
                    desc="When enabled, individual memorial owners can set their own tribute approval preference."
                    value={allowOwnerOverride}
                    onChange={setAllowOwnerOverride}
                    tag="Recommended"
                  />
                </div>
                <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-4">
                  <SafeIcon icon={FiInfo} className="text-blue-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-blue-700 leading-relaxed">
                    <strong>Priority order:</strong> Global settings override memorial-level settings unless "Allow Owner Override" is enabled.
                    Individual memorial owners can then choose to require manual approval even if global auto-approve is on.
                  </p>
                </div>
              </SectionCard>

              <SectionCard icon={FiZap} title="Conditional Auto-Approval" subtitle="Smart rules that auto-approve trusted submissions">
                <div className="space-y-3">
                  <ToggleRow
                    label="Auto-Approve Tributes from Verified Users"
                    desc="Logged-in users with verified email addresses skip the approval queue automatically."
                    value={autoApproveVerifiedUsers}
                    onChange={setAutoApproveVerifiedUsers}
                    tag="Recommended"
                  />
                  <ToggleRow
                    label="Require Email Verification to Submit"
                    desc="Guests must verify their email before posting any tribute or guestbook entry."
                    value={requireEmailVerification}
                    onChange={setRequireEmailVerification}
                  />
                  <ToggleRow
                    label="Allow Anonymous Tributes"
                    desc="Permit submissions without a name. These are always routed to manual review."
                    value={allowAnonymousTributes}
                    onChange={setAllowAnonymousTributes}
                  />
                </div>
              </SectionCard>
            </>
          )}

          {/* ── CONTENT FILTERS ────────────────────────────────────────── */}
          {activeSection === 'filters' && (
            <SectionCard icon={FiFilter} title="Automated Content Filters" subtitle="Sensitivity levels for AI-assisted content analysis">
              <div className="space-y-4">
                <SensitivitySlider
                  label="Profanity Filter"
                  desc="Blocks or flags messages containing inappropriate language."
                  value={profanityLevel}
                  onChange={setProfanityLevel}
                  levels={SENSITIVITY_LEVELS}
                />
                <SensitivitySlider
                  label="Spam Detection"
                  desc="Identifies repetitive, promotional or bot-generated content."
                  value={spamLevel}
                  onChange={setSpamLevel}
                  levels={SENSITIVITY_LEVELS}
                />
                <SensitivitySlider
                  label="Toxicity & Hate Speech"
                  desc="Detects hostile, offensive, or harmful language in tributes."
                  value={toxicityLevel}
                  onChange={setToxicityLevel}
                  levels={SENSITIVITY_LEVELS}
                />
              </div>

              <div className="border-t border-brand-dark pt-6 space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-4">Additional Filter Rules</p>
                <ToggleRow
                  label="Block External Links"
                  desc="Automatically reject any tribute containing URLs or hyperlinks."
                  value={linkBlocking}
                  onChange={setLinkBlocking}
                  tag="Recommended"
                />
                <ToggleRow
                  label="Excessive Caps Filter"
                  desc="Flag messages where more than 60% of characters are uppercase."
                  value={capsFilter}
                  onChange={setCapsFilter}
                />
                <ToggleRow
                  label="Duplicate Content Filter"
                  desc="Prevent the same message from being submitted multiple times to the same memorial."
                  value={duplicateFilter}
                  onChange={setDuplicateFilter}
                  tag="Recommended"
                />
              </div>

              {/* Visual sensitivity summary */}
              <div className="bg-accent-navy rounded-[24px] p-6 grid grid-cols-3 gap-4">
                {[
                  { label: 'Profanity', val: SENSITIVITY_LEVELS[profanityLevel].label },
                  { label: 'Spam',      val: SENSITIVITY_LEVELS[spamLevel].label      },
                  { label: 'Toxicity',  val: SENSITIVITY_LEVELS[toxicityLevel].label  },
                ].map(({ label, val }) => (
                  <div key={label} className="text-center">
                    <p className="text-[9px] font-black uppercase tracking-widest text-white/40">{label}</p>
                    <p className="text-lg font-serif text-accent-gold mt-1">{val}</p>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* ── BLOCKED WORDS ──────────────────────────────────────────── */}
          {activeSection === 'wordlist' && (
            <SectionCard icon={FiList} title="Custom Blocked Word List" subtitle="Exact words and phrases that trigger automatic rejection">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <SafeIcon icon={FiAlertTriangle} className="text-amber-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-amber-700 leading-relaxed">
                  Any tribute or guestbook entry containing a blocked word is <strong>automatically rejected</strong> and logged.
                  Words are matched case-insensitively. Use this for platform-specific terms not caught by the profanity filter.
                </p>
              </div>
              <WordlistEditor
                words={blockedWords}
                onAdd={w => setBlockedWords(prev => [...prev, w])}
                onRemove={w => setBlockedWords(prev => prev.filter(x => x !== w))}
              />
              <div className="flex items-center justify-between pt-2">
                <p className="text-[10px] text-text-muted">
                  {blockedWords.length} custom word{blockedWords.length !== 1 ? 's' : ''} in list
                </p>
                {blockedWords.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setBlockedWords([])}
                    className="text-[10px] text-red-500 font-bold hover:text-red-700 transition-colors uppercase tracking-widest flex items-center gap-1"
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-3 h-3" /> Clear All
                  </button>
                )}
              </div>
            </SectionCard>
          )}

          {/* ── AUTO-FLAGGING ──────────────────────────────────────────── */}
          {activeSection === 'flagging' && (
            <SectionCard icon={FiFlag} title="Auto-Flagging Rules" subtitle="Conditions that automatically mark content for admin review">
              <div className="space-y-3">
                <ToggleRow
                  label="Flag Tributes with External Links"
                  desc="Route any tribute containing a URL to the moderation queue for manual inspection."
                  value={flagExternalLinks}
                  onChange={setFlagExternalLinks}
                  tag="Recommended"
                />
                <ToggleRow
                  label="Flag Very Short Messages"
                  desc="Tributes shorter than the minimum character count are flagged as potentially low-quality."
                  value={flagShortMessages}
                  onChange={setFlagShortMessages}
                />
                <ToggleRow
                  label="Flag Repeated / Duplicate Submissions"
                  desc="When the same message is submitted more than once, flag all copies for review."
                  value={flagRepeatedContent}
                  onChange={setFlagRepeatedContent}
                  tag="Recommended"
                />
                <ToggleRow
                  label="Flag Anonymous Submissions"
                  desc="All tributes submitted without a name are automatically flagged."
                  value={flagAnonymous}
                  onChange={setFlagAnonymous}
                />
                <ToggleRow
                  label="Notify Site Admin on Auto-Flag"
                  desc="Send an in-app notification to Site Administrators whenever content is auto-flagged."
                  value={notifyAdminOnFlag}
                  onChange={setNotifyAdminOnFlag}
                  tag="Recommended"
                />
              </div>

              {/* Min message length */}
              <div className="p-5 bg-brand-light rounded-2xl border border-brand-dark space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-accent-navy text-sm">Minimum Message Length</p>
                    <p className="text-[10px] text-text-muted">Tributes shorter than this are flagged as low-quality.</p>
                  </div>
                  <span className="text-2xl font-serif text-accent-gold">{minMessageLength}</span>
                </div>
                <input
                  type="range" min="5" max="100" step="5"
                  value={minMessageLength}
                  onChange={e => setMinMessageLength(Number(e.target.value))}
                  className="w-full accent-[#C5A880]"
                />
                <div className="flex justify-between text-[9px] text-text-muted font-black uppercase tracking-widest">
                  <span>5 chars</span><span>50 chars</span><span>100 chars</span>
                </div>
              </div>

              {/* Flag summary */}
              <div className="bg-accent-navy rounded-[24px] p-6">
                <p className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-4">Active Flag Rules</p>
                <div className="space-y-2">
                  {[
                    { on: flagExternalLinks,   label: 'External links → Flagged'             },
                    { on: flagShortMessages,   label: `< ${minMessageLength} chars → Flagged` },
                    { on: flagRepeatedContent, label: 'Duplicate content → Flagged'           },
                    { on: flagAnonymous,       label: 'Anonymous submissions → Flagged'       },
                    { on: notifyAdminOnFlag,   label: 'Admin notified on each flag'           },
                  ].map(({ on, label }) => (
                    <div key={label} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${on ? 'bg-accent-gold' : 'bg-white/20'}`} />
                      <span className={`text-[10px] ${on ? 'text-white' : 'text-white/30 line-through'}`}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          )}

        </motion.div>
      </AnimatePresence>

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-4">
        <button
          type="button"
          onClick={resetToDefaults}
          className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-brand-dark text-text-muted rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-red-300 hover:text-red-500 transition-all"
        >
          <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
          Reset to Defaults
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="flex items-center gap-3 px-10 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-[0.98]"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          Save Moderation Settings
        </button>
      </div>

      <SaveToast show={showToast} />
    </div>
  );
};

export default ContentModerationSettings;